import {CustomSearch} from "../../../components/form/CustomSearch";
import styles from "../manage-access.module.scss";
import {CButton} from "../../../components/buttons";
import {AiOutlineReload} from "react-icons/ai";
import clsx from "clsx";
import {CustomMoreActions} from "../../../components/blocks/CustomMoreActions";
import {NotFound} from "../../../components/text";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {closePopUp, showPopUp} from "../../../redux/reducers/appReducer";
import {MODALS_NAMES} from "../../../constants";
import {CopyIconBtn} from "../../../components/blocks";
import {useDispatch} from "react-redux";
import {useHttp} from "../../../utils/hooks/http.hook";
import EmptyOrShow from "../../../components/EmptyOrShow";
import {useCustomSnackbar} from "../../../utils/hooks/useCustomSnackbar";
import {SelectedObjType} from "../types";
import {Employee, RequestResponse} from "../../../types";
import {getEmployeesData} from "../../userSettings/services";
import {CircularProgress} from "@material-ui/core";

const LIMIT = 30;
const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 400;

type SelectUserType = {
  selectedObj: SelectedObjType
  activeItemUsers: Employee[] | null
  resetData: () => void
  setSelectedObj: (n: SelectedObjType) => void
  isFetching?: boolean
}
export const SelectUser = ({activeItemUsers, setSelectedObj, selectedObj, isFetching, resetData}: SelectUserType) => {
  const dispatch = useDispatch();
  const {request} = useHttp();
  const {addDefaultSnack} = useCustomSnackbar();

  const [users, setUsers] = useState<Employee[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const isActiveItemMode = !!activeItemUsers?.length;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch((prev) => {
        const next = search || "";
        if (prev !== next) {
          setPage(1);
          setUsers([]);
        }
        return next;
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (isActiveItemMode) return;
    let cancelled = false;
    const isFirstPage = page === 1;

    const fetchUsers = async () => {
      isFirstPage ? setLoading(true) : setLoadingMore(true);
      const params: Record<string, any> = {page, limit: LIMIT};
      if (debouncedSearch.length >= MIN_SEARCH_LENGTH) {
        params.search = debouncedSearch;
      }
      const res = await getEmployeesData(params, () => {});
      if (cancelled) return;
      setUsers((prev) => isFirstPage ? res.employees : [...prev, ...res.employees]);
      setHasMore(page < (res.pagination?.total_pages || 0));
      setLoading(false);
      setLoadingMore(false);
    };
    fetchUsers();
    return () => { cancelled = true; };
  }, [page, debouncedSearch, isActiveItemMode]);

  const displayUsers = useMemo(() => {
    if (isActiveItemMode) {
      if (!search) return activeItemUsers;
      const term = search.toLowerCase();
      return activeItemUsers.filter((item) => item?.fio?.toLowerCase().includes(term));
    }
    return users;
  }, [isActiveItemMode, activeItemUsers, users, search]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLUListElement>) => {
    if (isActiveItemMode || loadingMore || loading || !hasMore) return;
    const {scrollTop, scrollHeight, clientHeight} = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      setPage((prev) => prev + 1);
    }
  }, [isActiveItemMode, loadingMore, loading, hasMore]);

  const handleChangeSearch = (e) => {
    setSearch(e.target.value);
  };

  const submitCopyUser = async (body, selectedObj: { id: number, name: string }) => {
    const res = await request<RequestResponse>('/core/employees/employee-permissions/duplicate/', 'post', body)
    if (res?.status) {
      setSelectedObj({
        type: "users",
        ...selectedObj, time: new Date().getTime()
      })
      dispatch(closePopUp());
      addDefaultSnack()
    }
  }

  const copyUser = async (employee) => {
    dispatch(
      showPopUp(MODALS_NAMES.duplicatePermissions, {
        onSubmit: async (body, selected) =>
          await submitCopyUser({
            ...body,
            from_employee_id: employee.employee_id,
          }, {id: selected.employee_id, name: selected.fio}),
        close: true,
        list: displayUsers?.filter(item => item.employee_id !== employee.employee_id),
        type: 'user',
        targetName: employee.fio
      })
    );
  };

  const usersActions = [
    {
      content: <CopyIconBtn title={'Дублировать доступы пользователя'}/>, onClick: (employee) => copyUser(employee)
    }
  ]

  return <EmptyOrShow loading={isFetching || (loading && !users.length)}>
    <CustomSearch handleChange={handleChangeSearch} value={search}/>
    <div
      className={`${styles.manage__menu_btnWrapper} ${styles.manage__menu_btnWrapperMargin}`}
    >
      <div/>
      <CButton
        disabled={isFetching}
        label={"Сброс"}
        className={`${styles.manage__menu_btn} ${styles.manage__menu_btnReset}`}
        onClick={resetData}
      >
        <AiOutlineReload color={"#ffffff"} width={25} height={25}/>
      </CButton>
    </div>
    {!!displayUsers?.length ? (
      <ul className={styles.manage__select_list} onScroll={handleScroll}>
        {displayUsers.map(item => (
          <li
            className={clsx(styles.manage__select_listItem, {
              [styles.manage__select_listItemActive]:
              selectedObj?.type === "users" &&
              selectedObj?.id === item?.employee_id
            })}
            key={item?.employee_id}
            onClick={() => {
              setSelectedObj({
                type: "users",
                id: item?.employee_id,
                name: item?.fio
              })
            }}
          >
            {item?.fio}
            <CustomMoreActions zeroPadding actions={usersActions} value={item}/>
          </li>
        ))}
        {loadingMore && (
          <li className={styles.manage__select_listItemLoading}>
            <CircularProgress size={18}/>
          </li>
        )}
      </ul>
    ) : (
      !loading && <NotFound/>
    )}
  </EmptyOrShow>
}